<template>
    <div class="wrap-content app-access-page">
        <div class="filter-section row-min-space">
            <div class="row">
                <div class="col-md-5 filter-title-section">
                    <h2>Application Access</h2>
                </div>
                <div class="col-md-7 text-right">
                    <div class="row">
                        <div class="col-md-6 col-xs-12">
                            <div class="form-group search-input">
                                <i class="icon-search"></i>
                                <b-form-input
                                    v-model="query.keyword"
                                    :placeholder="'Search'"
                                    autocomplete="no"
                                    @keyup.enter="applySearch('search')"
                                ></b-form-input>
                            </div>
                        </div>
                        <div class="col-md-6 col-xs-12 filter-btn-section">
                            <div class="row">
                                <div class="col-md-4">
                                    <button
                                        class="btn btn-primary"
                                        type="submit"
                                        :class="
                                            loading && showPageLoader
                                                ? 'disabled'
                                                : ''
                                        "
                                        @click="applySearch('search')"
                                    >
                                        <span class="btn-text">Apply</span>
                                        <span class="loader"></span>
                                    </button>
                                </div>
                                <div class="col-md-8">
                                    <b-button
                                        v-b-modal.add-application-popup
                                        class="btn secondary-add-btn"
                                        variant="secondary"
                                        type="submit"
                                        :class="{
                                            'button-disabled': !hasScopes(
                                                'manage-application-access'
                                            ),
                                        }"
                                    >
                                        <span class="btn-text"
                                            >+ Add Application</span
                                        >
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- user management table -->
        <div
            :class="[
                'table-section',
                'tablet-table-xs-min-width',
                'mobile-table-responsive-lg',
                {
                    'overflow-hidden': !items.length && !showPageLoader,
                },
            ]"
        >
            <b-table
                no-local-sorting
                :items="items"
                :fields="appAccessRecords"
                class="user-roles-table"
                @sort-changed="sortingChanged"
            >
                <!-- dataSegmentIds slot -->
                <template slot="dataSegmentIds" slot-scope="data">
                    {{ data.item.dataSegmentCodes }}
                </template>
                <!-- status slot -->
                <template slot="status" slot-scope="data">
                    <base-status :status-type="data.item.status"></base-status>
                </template>
                <!-- action slot -->
                <template slot="action" slot-scope="data">
                    <div
                        :class="[
                            'action-review',
                            {
                                disabled: !hasScopes(
                                    'manage-application-access'
                                ),
                            },
                        ]"
                    >
                        <!-- if action modify -->
                        <base-action
                            icon="icon-edit"
                            label="Modify"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('add-application-popup')
                            "
                        ></base-action>
                        <!-- if action archive -->
                        <base-action
                            v-if="data.item.status == 'active'"
                            icon="icon-trash"
                            label="Archive"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('archive-popup')
                            "
                        ></base-action>
                        <!-- if action activate -->
                        <base-action
                            v-if="data.item.status == 'archived'"
                            icon="icon-done"
                            label="Activate"
                            @click="
                                selectedRecord(data.item)
                                $bvModal.show('activate-popup')
                            "
                        ></base-action>
                    </div>
                </template>
            </b-table>
            <!-- When data is loading -->
            <base-table-spinner
                v-if="items.length == 0 && showPageLoader"
            ></base-table-spinner>
            <!-- When record not found -->
            <base-no-record-found
                v-if="items.length == 0 && !showPageLoader"
            ></base-no-record-found>
        </div>
        <pagination
            v-if="!showPageLoader"
            ref="pagination"
            :pagination="pagination"
            @pagination-changed="paginationChanged"
            @button-clicked-previous="applySearch('previous')"
            @button-clicked-next="applySearch('next')"
        ></pagination>
        <add-application-popup></add-application-popup>
        <archive-popup
            ref="archive-popup"
            record-type="application_access"
            @update-status="updateStatus"
        ></archive-popup>
        <activate-popup
            ref="activate-popup"
            record-type="application_access"
            @update-status="updateStatus"
        ></activate-popup>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import addApplicationPopup from '@/components/popups/AddApplicationPopup.vue'
import archivePopup from '@/components/popups/ArchivePopup.vue'
import pagination from '@/components/Pagination.vue'
import activatePopup from '@/components/popups/ActivatePopup.vue'
import Resource from '@/api/resource'
const apiResource = new Resource('access-control/api-access/')
import _ from 'lodash'

export default {
    components: {
        addApplicationPopup,
        archivePopup,
        pagination,
        activatePopup,
    },

    /*
    |--------------------------------------------------------------------------
    | Component > props
    |--------------------------------------------------------------------------
    */

    props: {}, // End of Component > props

    /*
    |--------------------------------------------------------------------------
    | Component > data
    |--------------------------------------------------------------------------
    */
    data() {
        return {
            query: {
                keyword: '',
                sortOrder: '',
                sortBy: '',
                startAt: 1,
                perPage: 10,
            },
            appAccessRecords: [],
            items: [],
            showPageLoader: true,
            pagination: {},
        }
    }, // End of Component > data

    /*
    |--------------------------------------------------------------------------
    | Component > computed
    |--------------------------------------------------------------------------
    */
    computed: {
        ...mapGetters([
            'applicationAccessFields',
            'applicationAccessList',
            'selectedItem',
            'reload',
            'loading',
            'hasScopes',
        ]),
    }, // End of Component > computed

    /*
    |--------------------------------------------------------------------------
    | Component > watch
    |--------------------------------------------------------------------------
    */
    watch: {
        reload(value) {
            if (value) {
                this.items = []
                this.query.startAt = 1
                this.getRecords()
            }
        },
    }, // End of Component > watch

    /*
    |--------------------------------------------------------------------------
    | Component > mounted
    |--------------------------------------------------------------------------
    */
    mounted() {
        this.getRecords()
    }, // End of Component > mounted

    /*
    |--------------------------------------------------------------------------
    | Component > methods
    |--------------------------------------------------------------------------
    */
    methods: {
        async getRecords() {
            this.appAccessRecords = this.applicationAccessFields
            this.showPageLoader = true
            let { data } = await apiResource.list(this.query)
            this.pagination = data.pagination
            this.pagination.totalRecords = data.data.length
            this.pagination.perPage = this.query.perPage
            data.data.forEach(function(val) {
                if (!val.dataSegmentIds.length) {
                    val.dataSegmentCodes = 'All'
                } else {
                    val.dataSegmentCodes = _.map(
                        val.dataSegments,
                        'code'
                    ).toString()
                }
            })
            data.data.forEach(function(val) {
                val.dataSegmentIds = _.map(val.dataSegments, '_id')
            })
            this.items = data.data
            this.showPageLoader = false
            this.$store.commit('setLoading', false)
        }, // End of getRecords() method

        sortingChanged(ctx) {
            // ctx.sortBy   ==> Field key for sorting by (or null for no sorting)
            this.query.sortBy = ctx.sortBy
            // ctx.sortDesc ==> true if sorting descending, false otherwise
            this.query.sortOrder = ctx.sortDesc ? 'desc' : 'asc'
            this.getRecords()
        }, // End of sortingChanged() method

        selectedRecord(record) {
            this.$store.commit('setItem', record)
        }, // End of selectedRecord() method

        async updateStatus(status) {
            let dataSegmentIds = this.selectedItem.dataSegmentIds
            if (!this.selectedItem.dataSegmentIds.length) {
                dataSegmentIds = ['all']
            }
            let params = {
                id: this.selectedItem._id,
                status: status,
                title: this.selectedItem.title,
                dataSegmentIds: dataSegmentIds,
            }
            await this.$store.dispatch('updateApplicationAccess', params)
        }, // End of updateStatus() method

        paginationChanged(type) {
            const perPage = type || 10
            this.query.perPage = perPage

            this.getRecords()
        }, // End of paginationChanged() method

        applySearch(type) {
            if (type && type == 'next') {
                this.query.startAt = this.pagination.next
            }

            if (type && type == 'previous') {
                this.query.startAt = this.pagination.current - 1
            }

            if (type && type == 'search') {
                this.query.startAt = 1
            }

            this.items = []
            this.showPageLoader = true
            if (type && type == 'search') {
                this.$store.commit('setLoading', true)
            }

            this.getRecords()
        }, // End of applySearch() method
    }, // End of Component > mounted
} // End of export default
</script>
